/**
 * Module 'accounts' (ADR 0067): which account or provider a Claude Code session ran on, found
 * automatically, so Spend can filter and break token cost down by account. Both agents may send
 * it as `Session.ext.account` (SessionAccount); the hub's costs module files each usage row
 * under its key and keeps the list of accounts it has seen (SpendAccount).
 *
 * Signals, strongest first (resolveAccount):
 *   1. 'credential-org'  a transcript's `credential_org` attachment names the organisation the
 *                        session's credential belongs to (exact, but Claude Code writes it only
 *                        sometimes);
 *   2. 'launch-env'      the environment of a session Orgus launched: Foundry, Bedrock, Vertex,
 *                        or an API key (identified only by a short irreversible fingerprint);
 *   3. 'config'          the config dir's signed-in account (`oauthAccount` in .claude.json) or
 *                        provider settings, as the agent saw it while the session worked;
 *   4. 'history'         the sign-in Claude Code's saved copies of .claude.json show at the
 *                        session's start, for a session that started before the agent first
 *                        looked (ADR 0067, amended 19 Oct 2026);
 *   5. 'message-id'      Bedrock (`msg_bdrk_`) and Vertex (`msg_vrtx_`) message ids.
 * And one the hub derives, never on a row: 'provider-report', an API key the Anthropic usage
 * report names (ADR 0067, amended 19 Oct 2026: API keys from the provider report). Claude Code
 * does not record whether a session used an API key or the sign-in, so the hub moves each key's
 * reported tokens out of the weakly attributed sign-in rows of that day and model (carveApiKeys
 * in the hub's costs module); agents can never send this source.
 * A config or history sign-in that contradicts the message ids (an Anthropic sign-in, Bedrock
 * ids) is not used: the ids are facts about the provider that answered.
 *
 * Privacy: never an email address, a person's name or a key. Personal organisations are named
 * after their owner ("jane@example.com's Organization"), so such a name is replaced by the plan.
 * Browser-safe, no dependencies.
 */
import type { Session, Ts } from '../index.ts';
import type { CostBucket } from './costs.ts';

/** The key under Session.ext. */
export const SESSION_ACCOUNT_EXT = 'account';

/**
 * ADR 0078 adds the accounts Codex and Gemini CLI run on: a ChatGPT sign-in ('chatgpt:<8 hex of
 * the account id's sha256>'), an OpenAI API key ('openai:<8 hex>'), a Google sign-in ('google:<8
 * hex of the account's subject id>') and a Gemini API key ('gemini:<8 hex>'). Vertex is 'vertex'
 * for either agent: one Google Cloud project, one bill.
 */
export type AccountKind = 'anthropic' | 'foundry' | 'bedrock' | 'vertex' | 'api-key' | 'chatgpt' | 'openai' | 'google' | 'gemini';
export const ACCOUNT_KINDS: readonly AccountKind[] = ['anthropic', 'foundry', 'bedrock', 'vertex', 'api-key', 'chatgpt', 'openai', 'google', 'gemini'];

/** 'subscription': seats (Pro, Max, Team), cost is notional. 'usage': billed per token. */
export type AccountBilling = 'subscription' | 'usage' | 'unknown';

export type AccountSource = 'credential-org' | 'launch-env' | 'config' | 'history' | 'message-id' | 'provider-report';

/**
 * Higher wins: a stronger signal re-files a session's rows, a weaker one never does. Hubs save
 * these numbers with each session's rows, so a new source slots in between (history is 1.5)
 * instead of renumbering the others. 'provider-report' is 0: it never files a row (its accounts
 * are a derived allocation), so it can never re-file one either.
 */
export const ACCOUNT_SOURCE_RANK: Record<AccountSource, number> = { 'credential-org': 4, 'launch-env': 3, config: 2, history: 1.5, 'message-id': 1, 'provider-report': 0 };

/** The account one session ran on. */
export interface SessionAccount {
  kind: AccountKind;
  /**
   * Stable id, namespaced by kind: 'anthropic:<org uuid>', 'foundry:<resource>',
   * 'bedrock:<profile>@<region>', 'vertex:<project>', 'api-key:<8 hex of sha256>'.
   */
  key: string;
  /** Automatic name ('Acme', 'Claude Max 20x', 'Foundry contoso'); null when none is known. */
  label: string | null;
  billing: AccountBilling;
  source: AccountSource;
}

/** An account as the hub lists it (CostsState.accounts). */
export interface SpendAccount {
  key: string;
  kind: AccountKind;
  /** The automatic label, never empty (accountLabel). */
  label: string;
  /** A person's rename, shown instead of the label. */
  name: string | null;
  billing: AccountBilling;
  /** The strongest signal any session gave for it. */
  source: AccountSource;
  firstSeen: Ts;
  lastSeen: Ts;
}

/** Spend of one account: the costs and finance roll-ups. key '' is the unknown account. */
export interface AccountBucket extends CostBucket {
  kind: AccountKind | null;
  billing: AccountBilling;
  /** The signal behind most of this account's spend (absent from older hubs). */
  source?: AccountSource;
  /**
   * USD moved out of this account's rows to API keys the provider report names (ADR 0067,
   * amended 19 Oct 2026): `cost` is already net of it. Absent when nothing moved.
   */
  movedOut?: number;
  /**
   * An API key from the provider report: USD of its reported usage that no session on this hub
   * could hold ("not matched to a session"), not part of `cost`. Absent when none.
   */
  unmatched?: number;
}

/**
 * What the agent knows of each Claude config folder it reads (ADR 0067, amended 19 Oct 2026),
 * an ExtMessage of module 'costs', sent once per connection and again when it changes. Only a
 * boolean per folder: whether `.claude.json` lists an approved custom API key
 * (`customApiKeyResponses.approved` is not empty). Never the key or its suffix.
 */
export const ACCOUNT_CONFIGS_TYPE = 'accounts.configs';
export interface AccountConfigsMessage {
  configs: { apiKeyApproved: boolean }[];
}

/** CostsState.apiKeys: whether API-key spend can be told apart (ADR 0067, amended 19 Oct 2026). */
export interface ApiKeySplit {
  /** The Anthropic usage report is connected (an Admin key is set): API keys come from it. */
  reportConnected: boolean;
  /** Some machine's Claude config lists an approved custom API key. */
  apiKeyApproved: boolean;
}

/**
 * The quiet hint under By account and Settings, Spend, Accounts: Claude Code does not record
 * whether a session used an API key, so without the provider report API-key spend sits under
 * the sign-in. Shown only when the report is not connected and there is a reason to think an
 * API key ran: an approved custom key in a config, or an account filed from sign-in history.
 */
export function showApiKeyHint(costs: { apiKeys?: ApiKeySplit; byAccount?: Pick<AccountBucket, 'source'>[]; accounts?: Pick<SpendAccount, 'source'>[] } | null | undefined): boolean {
  if (!costs || costs.apiKeys?.reportConnected) return false;
  if (costs.apiKeys?.apiKeyApproved) return true;
  return (costs.byAccount ?? []).some((b) => b.source === 'history') || (costs.accounts ?? []).some((a) => a.source === 'history');
}

/**
 * Accounts of sessions that left the agent's lookback window (ADR 0067, amended 19 Oct 2026):
 * the agent no longer reports them as sessions, so when it learns their account later (from
 * sign-in history) it sends them in batches of at most PAST_ACCOUNTS_MAX, an ExtMessage of
 * module 'costs'. The hub re-files only rows of that machine, and only for a stronger source.
 */
export const PAST_ACCOUNTS_TYPE = 'accounts.past';
export const PAST_ACCOUNTS_MAX = 200;
export interface PastSessionAccount {
  sessionId: string;
  account: SessionAccount;
  lastActivity: Ts;
}
export interface PastAccountsMessage {
  sessions: PastSessionAccount[];
}

/** The `account` query value (finance report) for sessions with no known account. */
export const UNKNOWN_ACCOUNT = 'unknown';
export const UNKNOWN_ACCOUNT_LABEL = 'Unknown account';

/** PUT /api/costs/accounts/:key. An empty name clears the rename. */
export interface AccountRenameInput {
  name: string;
}
export const ACCOUNT_NAME_MAX = 60;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KEY = /^(anthropic|foundry|bedrock|vertex|api-key|chatgpt|openai|google|gemini):[A-Za-z0-9._@:/-]{1,120}$/;
/** The sources an agent may send. 'provider-report' is the hub's own (cleanAccount's `derived`). */
const SOURCES: readonly AccountSource[] = ['credential-org', 'launch-env', 'config', 'history', 'message-id'];
const BILLINGS: readonly AccountBilling[] = ['subscription', 'usage', 'unknown'];

export function isAccountKey(key: unknown): key is string {
  return typeof key === 'string' && KEY.test(key);
}

/** Session.ext.account when the agent sent a well-formed one. */
export function sessionAccount(s: Pick<Session, 'ext'> | null | undefined): SessionAccount | null {
  return cleanAccount(s?.ext?.[SESSION_ACCOUNT_EXT]);
}

/**
 * A SessionAccount from untrusted input (a message, a saved file), or null. `derived` also
 * accepts 'provider-report' (the hub's own saved accounts): an agent can never send it.
 */
export function cleanAccount(raw: unknown, opts: { derived?: boolean } = {}): SessionAccount | null {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Record<string, unknown>;
  if (!ACCOUNT_KINDS.includes(a.kind as AccountKind) || !isAccountKey(a.key) || !(a.key as string).startsWith(`${a.kind as string}:`)) return null;
  if (!SOURCES.includes(a.source as AccountSource) && !(opts.derived && a.source === 'provider-report')) return null;
  const label = typeof a.label === 'string' ? cleanLabel(a.label) : null;
  return {
    kind: a.kind as AccountKind,
    key: a.key as string,
    label,
    billing: BILLINGS.includes(a.billing as AccountBilling) ? (a.billing as AccountBilling) : 'unknown',
    source: a.source as AccountSource,
  };
}

/** One line, at most 80 characters, and never an email address. */
export function cleanLabel(label: string): string | null {
  const one = label.replace(/\s+/g, ' ').trim().slice(0, 80);
  if (!one || /@/.test(one)) return null;
  return one;
}

/**
 * A short id of an account key, for labels: the last 6 characters of a provider-report key id
 * ('apikey_01Rj2N8SVvo6BePZj99NhmiT' gives '9NhmiT': its head is a timestamp, alike for keys
 * made together), else the first 8 characters.
 */
export function accountShortId(key: string): string {
  const id = key.slice(key.indexOf(':') + 1);
  return /^apikey_/.test(id) ? id.slice(-6) : id.slice(0, 8);
}

/** The account key of an API key the Anthropic usage report names by id. */
export function reportKeyAccount(keyId: string): string {
  return `api-key:${keyId}`;
}

/** The name to show for an account: the rename, else the automatic label, else one from its key. */
export function accountLabel(a: { kind: AccountKind; key: string; label?: string | null; name?: string | null }): string {
  if (a.name) return a.name;
  if (a.label) return a.label;
  const id = a.key.slice(a.key.indexOf(':') + 1);
  switch (a.kind) {
    case 'anthropic':
      return `Anthropic org ${id.slice(0, 8)}`;
    case 'foundry':
      return id === 'default' ? 'Azure Foundry' : `Foundry ${id}`;
    case 'bedrock':
      return id === 'default' ? 'Amazon Bedrock' : `Bedrock ${id}`;
    case 'vertex':
      return id === 'default' ? 'Google Vertex AI' : `Vertex ${id}`;
    case 'api-key':
      return `API key ${accountShortId(a.key)}`;
    case 'chatgpt':
      return `ChatGPT ${id.slice(0, 8)}`;
    case 'openai':
      return `OpenAI key ${id.slice(0, 8)}`;
    case 'google':
      return `Google ${id.slice(0, 8)}`;
    case 'gemini':
      return `Gemini key ${id.slice(0, 8)}`;
  }
}

export const ACCOUNT_KIND_LABEL: Record<AccountKind, string> = {
  anthropic: 'Anthropic',
  foundry: 'Azure Foundry',
  bedrock: 'Amazon Bedrock',
  vertex: 'Google Vertex AI',
  'api-key': 'API key',
  chatgpt: 'ChatGPT',
  openai: 'OpenAI API key',
  google: 'Google',
  gemini: 'Gemini API key',
};

export const ACCOUNT_BILLING_LABEL: Record<AccountBilling, string> = {
  subscription: 'Subscription',
  usage: 'Usage billed',
  unknown: 'Billing unknown',
};

const PLAN: Record<string, string> = {
  claude_max: 'Claude Max',
  claude_pro: 'Claude Pro',
  claude_team: 'Claude Team',
  claude_enterprise: 'Claude Enterprise',
  api: 'Anthropic Console',
  console: 'Anthropic Console',
};

/** 'Claude Max 20x' from organizationType and a rate limit tier ('default_claude_max_20x'). */
export function planLabel(orgType: unknown, tier?: unknown): string | null {
  const t = typeof orgType === 'string' ? orgType.toLowerCase() : '';
  const base = PLAN[t] ?? null;
  if (!base) return null;
  const m = typeof tier === 'string' ? /max_(\d{1,3})x/i.exec(tier) : null;
  return t === 'claude_max' && m ? `${base} ${m[1]}x` : base;
}

/**
 * How an organisation pays, from `oauthAccount`'s billingType, organizationType and seatTier.
 * The mapping, explicitly (anything else is unknown, never a guess):
 *   - API and Console organisations                                       usage
 *   - a contracted billing type ('stripe_subscription_contracted') or a
 *     usage-based seat tier ('enterprise_usage_based'): billed per use      usage
 *   - Claude Max and Claude Pro (sold only as subscriptions)               subscription
 *   - Claude Team on a plain subscription ('stripe_subscription')          subscription
 *   - an explicit usage, prepaid, postpaid or metered billing type          usage
 */
export function billingOf(billingType: unknown, orgType: unknown, seatTier?: unknown): AccountBilling {
  const b = typeof billingType === 'string' ? billingType.toLowerCase() : '';
  const t = typeof orgType === 'string' ? orgType.toLowerCase() : '';
  const seat = typeof seatTier === 'string' ? seatTier.toLowerCase() : '';
  if (t === 'api' || t === 'console') return 'usage';
  if (b.includes('contracted') || seat.includes('usage_based')) return 'usage';
  if (t === 'claude_max' || t === 'claude_pro') return 'subscription';
  if (t === 'claude_team' && b === 'stripe_subscription') return 'subscription';
  if (/^(usage|usage_based|prepaid|postpaid|metered)$/.test(b)) return 'usage';
  return 'unknown';
}

/**
 * The account a config dir is signed in to, from `oauthAccount` of its .claude.json. Reads only
 * the organisation's uuid, name, type, billing type and rate limit tier: never the email, the
 * person's name or uuid. Null without an organisation uuid.
 */
export function accountFromOauth(oauth: unknown): SessionAccount | null {
  if (!oauth || typeof oauth !== 'object') return null;
  const o = oauth as Record<string, unknown>;
  const uuid = typeof o.organizationUuid === 'string' ? o.organizationUuid.toLowerCase() : '';
  if (!UUID.test(uuid)) return null;
  const plan = planLabel(o.organizationType, o.userRateLimitTier ?? o.organizationRateLimitTier);
  const name = typeof o.organizationName === 'string' ? o.organizationName : '';
  // A personal organisation carries its owner's email or name: show the plan instead.
  const personal = !name || /@/.test(name) || /'s organi[sz]ation$/i.test(name.trim());
  const label = personal ? plan : (cleanLabel(name) ?? plan);
  return { kind: 'anthropic', key: `anthropic:${uuid}`, label, billing: billingOf(o.billingType, o.organizationType, o.seatTier), source: 'config' };
}

/** The provider a message id names: Bedrock and Vertex mark theirs; a plain 'msg_' proves nothing. */
export function providerOfMessageId(id: unknown): 'bedrock' | 'vertex' | null {
  if (typeof id !== 'string') return null;
  if (id.startsWith('msg_bdrk_')) return 'bedrock';
  if (id.startsWith('msg_vrtx_')) return 'vertex';
  return null;
}

/** What an agent knows about one session's account. */
export interface AccountSignals {
  /** organizationUuid of the newest credential_org attachment in the transcript. */
  credentialOrg?: string | null;
  /** The account its launch environment named (sessions Orgus started). */
  launch?: SessionAccount | null;
  /** The config dir's account while the session worked. */
  config?: SessionAccount | null;
  /** The sign-in saved config copies show at the session's start (sessions before the first look). */
  history?: SessionAccount | null;
  /** The provider its newest marked message id names. */
  messageProvider?: 'bedrock' | 'vertex' | null;
  /** What the agent knows of an organisation by uuid (from config snapshots), for credential_org. */
  org?: (uuid: string) => Pick<SessionAccount, 'label' | 'billing'> | null;
}

/** One session's account, strongest signal first (see the module comment). Null when nothing says. */
export function resolveAccount(sig: AccountSignals): SessionAccount | null {
  const org = typeof sig.credentialOrg === 'string' ? sig.credentialOrg.toLowerCase() : '';
  if (UUID.test(org)) {
    const known = sig.org?.(org) ?? null;
    return { kind: 'anthropic', key: `anthropic:${org}`, label: known?.label ?? null, billing: known?.billing ?? 'unknown', source: 'credential-org' };
  }
  if (sig.launch) return { ...sig.launch, source: 'launch-env' };
  const provider = sig.messageProvider ?? null;
  if (sig.config && (!provider || sig.config.kind === provider)) return { ...sig.config, source: 'config' };
  if (sig.history && (!provider || sig.history.kind === provider)) return { ...sig.history, source: 'history' };
  if (provider) return { kind: provider, key: `${provider}:default`, label: null, billing: 'usage', source: 'message-id' };
  return null;
}
